import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FundingApplicationService } from '@/services/funding-application.service';
import {
  FundingApplication,
  FundingApplicationStatus,
  FundingProgram,
  FundingProgramLabels,
  FundingApplicationStatusLabels,
  FundingTypeLabels,
} from '@/models/funding-application.model';

export const FundingApplications: React.FC = () => {
  const [applications, setApplications] = useState<FundingApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FundingApplicationStatus | 'all'>('all');
  const [filterProgram, setFilterProgram] = useState<FundingProgram | 'all'>('all');

  useEffect(() => {
    loadApplications();
  }, [filterStatus, filterProgram]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      let allApps = await FundingApplicationService.getAll();
      
      if (filterStatus !== 'all') {
        allApps = allApps.filter(app => app.status === filterStatus);
      }
      
      if (filterProgram !== 'all') {
        allApps = allApps.filter(app => app.program === filterProgram);
      }
      
      setApplications(allApps);
    } catch (error) {
      console.error('Error loading funding applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getStatusColor = (status: FundingApplicationStatus) => {
    switch (status) {
      case FundingApplicationStatus.DRAFT:
        return 'bg-gray-100 text-gray-800';
      case FundingApplicationStatus.SUBMITTED:
        return 'bg-blue-100 text-blue-800';
      case FundingApplicationStatus.UNDER_REVIEW:
        return 'bg-yellow-100 text-yellow-800';
      case FundingApplicationStatus.APPROVED:
        return 'bg-green-100 text-green-800';
      case FundingApplicationStatus.REJECTED:
        return 'bg-red-100 text-red-800';
      case FundingApplicationStatus.WITHDRAWN:
        return 'bg-gray-100 text-gray-800';
      case FundingApplicationStatus.FUNDED:
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTotalRequested = () => {
    return applications.reduce((sum, app) => sum + app.requestedAmount, 0);
  };

  const getTotalFunded = () => {
    return applications
      .filter(app => app.fundedAmount)
      .reduce((sum, app) => sum + (app.fundedAmount || 0), 0);
  };

  const getTotalApproved = () => {
    return applications
      .filter(app => app.approvedAmount)
      .reduce((sum, app) => sum + (app.approvedAmount || 0), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading funding applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Funding Applications</h1>
            <p className="mt-1 text-sm text-gray-500">Track and manage funding applications for FairCare operations</p>
          </div>
          <Link
            to="/financial/funding/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            + New Application
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Requested</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(getTotalRequested())}</p>
            <p className="text-xs text-gray-500 mt-1">{applications.length} applications</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Approved</p>
            <p className="text-2xl font-bold text-green-600 mt-2">{formatCurrency(getTotalApproved())}</p>
            <p className="text-xs text-gray-500 mt-1">
              {applications.filter(app => app.approvedAmount).length} approved
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Funded</p>
            <p className="text-2xl font-bold text-emerald-600 mt-2">{formatCurrency(getTotalFunded())}</p>
            <p className="text-xs text-gray-500 mt-1">
              {applications.filter(app => app.fundedAmount).length} funded
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as FundingApplicationStatus | 'all')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="all">All Statuses</option>
                {Object.values(FundingApplicationStatus).map((status) => (
                  <option key={status} value={status}>
                    {FundingApplicationStatusLabels[status]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Program</label>
              <select
                value={filterProgram}
                onChange={(e) => setFilterProgram(e.target.value as FundingProgram | 'all')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="all">All Programs</option>
                {Object.values(FundingProgram).map((program) => (
                  <option key={program} value={program}>
                    {FundingProgramLabels[program]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Program
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Application Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Follow-up
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-500">
                    No funding applications found. <Link to="/financial/funding/new" className="text-blue-600 hover:text-blue-800">Create one</Link> to get started.
                  </td>
                </tr>
              ) : (
                applications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {application.program === FundingProgram.OTHER
                          ? application.programName
                          : FundingProgramLabels[application.program]}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {FundingTypeLabels[application.fundingType]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(application.requestedAmount)}
                      </div>
                      {application.approvedAmount && (
                        <div className="text-xs text-green-600">
                          Approved: {formatCurrency(application.approvedAmount)}
                        </div>
                      )}
                      {application.fundedAmount && (
                        <div className="text-xs text-emerald-600">
                          Funded: {formatCurrency(application.fundedAmount)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          application.status
                        )}`}
                      >
                        {FundingApplicationStatusLabels[application.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(application.applicationDate as Date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {application.nextFollowUpDate
                        ? formatDate(application.nextFollowUpDate as Date)
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/financial/funding/${application.id}`}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        View
                      </Link>
                      <Link
                        to={`/financial/funding/${application.id}/edit`}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

